import { z } from 'zod'
import { api } from '@/lib/api'
import { authUserSchema } from './types'
import type { AuthUser } from './types'

/**
 * Auth endpoints. The backend sets/clears httpOnly cookies, so this layer
 * only deals in user identity, not tokens.
 */

export interface LoginRequest {
  email: string
  password: string
}

export async function login(payload: LoginRequest): Promise<AuthUser> {
  const raw = await api.post<unknown>('/auth/login', payload as unknown as Record<string, unknown>)
  return authUserSchema.parse(raw)
}

export async function logout(): Promise<void> {
  return api.post<void>('/auth/logout')
}

export async function getCurrentUser(): Promise<AuthUser> {
  const raw = await api.get<unknown>('/auth/me')
  return authUserSchema.parse(raw)
}

export async function updateProfile(input: { name: string }): Promise<AuthUser> {
  const raw = await api.patch<unknown>('/auth/me', input as unknown as Record<string, unknown>)
  return authUserSchema.parse(raw)
}

export async function changePassword(input: {
  current_password: string
  new_password: string
}): Promise<void> {
  return api.post<void>('/auth/me/change-password', input as unknown as Record<string, unknown>)
}

export const forgotPasswordResponseSchema = z.object({
  message: z.string(),
  reset_url: z.string().nullable().optional(),
})
export type ForgotPasswordResponse = z.infer<typeof forgotPasswordResponseSchema>

export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
  const raw = await api.post<unknown>('/auth/forgot-password', { email } as unknown as Record<string, unknown>)
  return forgotPasswordResponseSchema.parse(raw)
}

export async function resetPassword(token: string, new_password: string): Promise<void> {
  await api.post<unknown>('/auth/reset-password', { token, new_password } as unknown as Record<string, unknown>)
}
